import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Clock, MinusCircle, RefreshCcw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { OrderPayment } from "@tilt/pos-sdk";

import { getPosClient } from "../../lib/api";
import { formatCents, formatDateTimeWithYear } from "../../lib/format";
import {
  lookupMethodByTypeSubtype,
  resolveMethodIcon,
  resolveMethodLabel,
} from "../../lib/payment-methods";
import { useActiveTimezone } from "../../lib/useActiveTimezone";
import { cn } from "../../lib/utils";
import { PaymentStatusBadge } from "../ui/StatusBadge";
import { TimelinePaymentDrawer } from "./TimelinePaymentDrawer";

export interface TimelineProps {
  readonly payments: readonly OrderPayment[];
  readonly currency: string;
  readonly locationId?: string | null | undefined;
  /** Required when ``orderId`` is also supplied - passed to the
   *  /lifecycle GET endpoint so the per-row "Show history" disclosure
   *  can lazy-load events. Omitted on read-only views that don't
   *  carry the order context (e.g. cross-order recent-payments list). */
  readonly orderId?: string | undefined;
  /** When set, the row for any non-terminal charge gets a quiet
   *  "Cancel" link that calls back with the payment id. Caller is
   *  responsible for opening the confirmation dialog + dispatching
   *  ``cancelPayment``. Omitted on read-only views (Refund history
   *  on an already-paid order, etc.). */
  readonly onCancelPayment?: ((paymentId: string) => void) | undefined;
  /** Called when the cashier clicks "Print receipt" in the payment
   *  drawer. Caller (OrderDetailPage) opens the print dialog with
   *  the full order envelope. Omitted on read-only views. */
  readonly onPrintReceipt?: (() => void) | undefined;
  /** Pre-fills the SMS field in the payment-link panel inside the
   *  payment drawer. Sourced from the order's attached customer phone. */
  readonly customerPhone?: string | null | undefined;
}

/** Above this many top-level rows, the timeline starts collapsed and
 *  reveals the rest behind a "Show all N" affordance. Picked so a
 *  3-tender split (cash + check + card) always shows in full. */
const COLLAPSE_THRESHOLD = 6;

const STATUS_DOT: Record<string, string> = {
  APPROVED: "bg-emerald-500",
  SETTLED: "bg-emerald-500",
  PENDING: "bg-amber-500",
  ACTION_REQUIRED: "bg-amber-500",
  DECLINED: "bg-red-500",
  FAILED: "bg-red-500",
};

interface Group {
  readonly charge: OrderPayment;
  readonly refunds: readonly OrderPayment[];
}

function groupByCharge(payments: readonly OrderPayment[]): readonly Group[] {
  // Bucket charges in chronological order; attach refunds to their
  // parent. Refunds whose parent isn't in the list (shouldn't happen
  // - refunds always reference an order-linked charge - but we
  // tolerate it) get bucketed as orphan top-level rows so they don't
  // disappear silently.
  const charges: OrderPayment[] = [];
  const refundsByParent = new Map<string, OrderPayment[]>();
  const orphans: OrderPayment[] = [];

  for (const p of payments) {
    if (p.role === "charge") {
      charges.push(p);
      continue;
    }
    if (p.parent_payment_id) {
      const list = refundsByParent.get(p.parent_payment_id) ?? [];
      list.push(p);
      refundsByParent.set(p.parent_payment_id, list);
    } else {
      orphans.push(p);
    }
  }

  const groups: Group[] = charges.map((c) => ({
    charge: c,
    refunds: refundsByParent.get(c.payment_id) ?? [],
  }));

  // Refunds whose parent isn't on this order rendered standalone - preserves
  // visibility without misattributing. Treat each as its own pseudo-group.
  for (const r of orphans) {
    groups.push({ charge: r, refunds: [] });
  }
  return groups;
}

/** Payment timeline - chronological list of charges + their refund
 *  children. Refunds are nested under their parent charge so the
 *  cashier can read each charge's net at a glance. The leading icon
 *  mirrors method (cash, check) and the status pill on the right
 *  tells the cashier "did this clear?". Refund rows render as
 *  negatives so they read as ledger entries.
 *
 *  Timelines longer than ``COLLAPSE_THRESHOLD`` charges start
 *  collapsed showing the most recent N; "Show all" reveals the rest. */
export function Timeline({
  payments,
  currency,
  locationId,
  orderId,
  onCancelPayment,
  onPrintReceipt,
  customerPhone,
}: TimelineProps) {
  const { t } = useTranslation();
  const allGroups = useMemo(() => groupByCharge(payments), [payments]);
  const [expanded, setExpanded] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);

  const cancelledCount = useMemo(
    () => allGroups.filter((g) => !!g.charge.cancelled_at).length,
    [allGroups],
  );
  const groups = useMemo(
    () => (showCancelled ? allGroups : allGroups.filter((g) => !g.charge.cancelled_at)),
    [allGroups, showCancelled],
  );

  // Empty state - when the order is fresh (no payments collected
  // yet) the timeline used to render nothing. In the SalePage's
  // right rail that left a confusing gap; surface a friendly hint
  // instead so the cashier sees the panel exists and will populate
  // as soon as they take the first payment.
  if (groups.length === 0) {
    return (
      <div>
        <h2 className="mb-3 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
          {t("transactions.timelineHeading")}
        </h2>
        <div className="rounded-lg bg-surface-muted px-4 py-6 text-center">
          <Clock className="mx-auto h-5 w-5 text-faint" aria-hidden="true" />
          <p className="mt-2 text-sm text-muted">{t("sale.timelineEmpty")}</p>
        </div>
        {cancelledCount > 0 && (
          <button
            type="button"
            onClick={() => setShowCancelled((v) => !v)}
            className="mt-2 text-xs text-muted underline-offset-2 hover:underline"
          >
            {showCancelled
              ? t("sale.timelineHideCancelled")
              : t("sale.timelineShowCancelled", { count: cancelledCount })}
          </button>
        )}
      </div>
    );
  }

  const overflowed = groups.length > COLLAPSE_THRESHOLD;
  const visible = expanded || !overflowed ? groups : groups.slice(-COLLAPSE_THRESHOLD);
  const hiddenCount = groups.length - visible.length;

  return (
    <div>
      <h2 className="mb-3 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
        {t("transactions.timelineHeading")}
      </h2>
      <ol className="divide-y divide-line/60 overflow-hidden rounded-lg ring-1 ring-line/60">
        {overflowed && !expanded && (
          <li className="bg-surface-muted px-4 py-2 text-center text-xs text-muted">
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="inline-flex items-center gap-1 font-medium text-brand-primary hover:underline"
            >
              <ChevronDown className="h-3 w-3" />
              {t("sale.timelineShowAll", { count: hiddenCount })}
            </button>
          </li>
        )}
        {visible.map((g) => (
          <TimelineGroup
            key={g.charge.link_id}
            group={g}
            currency={currency}
            locationId={locationId}
            orderId={orderId}
            onCancelPayment={onCancelPayment}
            onPrintReceipt={onPrintReceipt}
            customerPhone={customerPhone}
          />
        ))}
      </ol>
      {cancelledCount > 0 && (
        <button
          type="button"
          onClick={() => setShowCancelled((v) => !v)}
          className="mt-2 text-xs text-muted underline-offset-2 hover:underline"
        >
          {showCancelled
            ? t("sale.timelineHideCancelled")
            : t("sale.timelineShowCancelled", { count: cancelledCount })}
        </button>
      )}
    </div>
  );
}

function TimelineGroup({
  group,
  currency,
  locationId,
  orderId,
  onCancelPayment,
  onPrintReceipt,
  customerPhone,
}: {
  group: Group;
  currency: string;
  locationId?: string | null | undefined;
  orderId?: string | undefined;
  onCancelPayment?: ((paymentId: string) => void) | undefined;
  onPrintReceipt?: (() => void) | undefined;
  customerPhone?: string | null | undefined;
}) {
  const { t } = useTranslation();
  const refundedCents = group.refunds.reduce((sum, r) => sum + r.applied_amount_cents, 0);
  const netCents = group.charge.applied_amount_cents - refundedCents;
  return (
    <li className="bg-surface">
      <PaymentRow
        payment={group.charge}
        currency={currency}
        locationId={locationId}
        indent={0}
        orderId={orderId}
        onCancelPayment={onCancelPayment}
        onPrintReceipt={onPrintReceipt}
        customerPhone={customerPhone}
      />
      {group.refunds.map((r) => (
        <PaymentRow
          key={r.link_id}
          payment={r}
          currency={currency}
          locationId={locationId}
          indent={1}
          orderId={orderId}
          onPrintReceipt={onPrintReceipt}
          customerPhone={customerPhone}
        />
      ))}
      {group.refunds.length > 0 && (
        <div className="flex items-center justify-end gap-2 bg-surface-muted px-4 py-1.5 pl-12 text-xs">
          <span className="text-muted">{t("sale.netForCharge")}</span>
          <span
            className={cn(
              "font-semibold tabular-nums",
              netCents === 0 ? "text-foreground" : "text-strong",
            )}
          >
            {formatCents(netCents, currency)}
          </span>
        </div>
      )}
    </li>
  );
}

function PaymentRow({
  payment: p,
  currency,
  locationId,
  indent,
  orderId,
  onCancelPayment,
  onPrintReceipt,
  customerPhone,
}: {
  readonly payment: OrderPayment;
  readonly currency: string;
  readonly locationId?: string | null | undefined;
  readonly indent: 0 | 1;
  readonly orderId?: string | undefined;
  readonly onCancelPayment?: ((paymentId: string) => void) | undefined;
  readonly onPrintReceipt?: (() => void) | undefined;
  readonly customerPhone?: string | null | undefined;
}) {
  const { t } = useTranslation();
  const tz = useActiveTimezone();
  const railsQuery = useQuery({
    enabled: locationId != null,
    queryKey: ["payment-rails", locationId],
    queryFn: () => getPosClient().getLocationPaymentRails(locationId!),
    staleTime: 5 * 60_000,
  });
  const rails = railsQuery.data?.rails ?? [];
  const rail = lookupMethodByTypeSubtype(rails, p.payment_type, p.payment_subtype ?? null);
  // Drawer holds full details - external_ref, payment_link, lifecycle
  // history, cancellation context, cancel action. The row itself is a
  // single condensed line so the right rail (~280px wide) reads
  // cleanly. Earlier inline disclosure stacked everything in the row
  // and ran out of horizontal room (2026-05-02 dogfood).
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isCancelled = !!p.cancelled_at;
  const dotClass = isCancelled ? "bg-muted" : (STATUS_DOT[p.status] ?? "bg-muted");
  const canOpenDrawer = orderId !== undefined;
  // Hover tooltips on the leading icon + status dot - without an
  // inline text label the cashier shouldn't have to memorise the
  // glyph → method / colour → status mapping (2026-05-03 dogfood -
  // "hovering on the icon and status circle should show some text").
  // Method label is the capitalised payment_type ("Cash" / "Ach" /
  // …) with "Refund" appended on the role=refund + "Cancelled" when
  // the row is cancelled.
  const railLabel = rail
    ? resolveMethodLabel(rail, t)
    : p.payment_type.charAt(0).toUpperCase() + p.payment_type.slice(1);
  const subtypeLabel = p.payment_subtype && rail ? resolveMethodLabel(rail, t) : null;
  const methodTitle = (() => {
    if (p.role === "refund") return `${railLabel} ${t("sale.refund")}`;
    if (isCancelled) return `${railLabel} - ${t("sale.timelineCancelledAt")}`;
    return railLabel;
  })();
  const statusTitle = isCancelled ? t("sale.timelineCancelledAt") : p.status;
  return (
    <div
      className={cn(
        // ``flex-wrap`` lets the right-side amount/badge cluster drop
        // to a second line when the container is narrow (cashier
        // sale-page rail, mobile, embedded inline-expand at smaller
        // viewports). Without wrap, the badge gets clipped - see
        // dogfood screenshot 2026-05-04 where "CANCELLED" rendered
        // as just "CA". Two halves separated by a flex-1 spacer:
        // wide → both sit on one line side-by-side; narrow → right
        // half wraps below.
        "flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5",
        indent === 1 && "border-l-2 border-line pl-8",
      )}
    >
      {/* LEFT half - icon + status dot + (refund/method label) + date.
          Naturally sized by content (no flex-1) so the right half can
          sit next to it on the same line. flex-nowrap prevents the
          date from wrapping internally - the full date shows on one
          line; the right half wraps to the next line if there isn't
          room for both halves. */}
      <div className="flex flex-nowrap items-center gap-2">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-muted text-muted"
          title={methodTitle}
          aria-label={methodTitle}
        >
          {/* Method icon stays even on cancelled rows - operator
              dogfood: "we don't see the original payment type icon or
              name". The cancelled state is conveyed by the status dot
              + amount strikethrough + PaymentStatusBadge ("CANCELLED");
              losing the method glyph made the row read as generic
              "rejected" without saying which method was being attempted. */}
          {p.role === "refund" ? (
            <RefreshCcw className="h-3.5 w-3.5" />
          ) : (
            (() => {
              const Icon = rail ? resolveMethodIcon(rail) : MinusCircle;
              return <Icon className="h-3.5 w-3.5" aria-hidden />;
            })()
          )}
        </span>
        <span
          className={cn("h-2 w-2 shrink-0 rounded-full", dotClass)}
          title={statusTitle}
          aria-label={statusTitle}
        />
        {p.role === "refund" && (
          <span className="shrink-0 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            {t("sale.refund")}
          </span>
        )}
        {/* Subtype label - always shown when a meaningful subtype exists
            (card_present / virtual / pay_by_text). Cancelled rows show
            the full type name; normal rows show subtype only. */}
        {p.role !== "refund" && subtypeLabel && (
          <span className="shrink-0 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            {isCancelled ? `${railLabel} · ${subtypeLabel}` : subtypeLabel}
          </span>
        )}
        {isCancelled && p.role !== "refund" && !subtypeLabel && (
          <span className="shrink-0 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            {railLabel}
          </span>
        )}
        <span className="text-[0.6875rem] tabular-nums text-faint">
          {formatDateTimeWithYear(p.created_at, tz)}
        </span>
      </div>
      {/* RIGHT half - amount + status pill + chevron, all on one line.
          Earlier shape stacked amount over pill (``flex-col``); on the
          narrow sale rail the pill ended up half-overlapping the
          struck-through amount and read as a single garbled glyph
          (2026-05-04 dogfood). Now the amount and the (compact) pill
          sit side-by-side: when the row is wide they share a line at
          the right edge, when the OUTER row wraps the whole right
          cluster drops to the next line as a unit - never separated,
          never overlapping. ``flex-nowrap`` on the inner cluster is
          load-bearing: with flex-wrap here the pill could wrap onto a
          line below the amount on extreme-narrow widths, defeating
          the fix. The outer row's flex-wrap (line 264) handles
          narrow-row wrapping at the cluster boundary. */}
      <div className="ml-auto flex shrink-0 flex-nowrap items-center gap-2">
        <span
          className={cn(
            "text-sm font-semibold tabular-nums",
            p.role === "refund"
              ? "text-red-600"
              : isCancelled
                ? "text-muted line-through"
                : "text-strong",
          )}
        >
          {p.role === "refund" ? "−" : ""}
          {formatCents(p.applied_amount_cents, currency)}
        </span>
        <PaymentStatusBadge status={p.status} size="sm" />
        {/* Single Details affordance - opens the drawer with the full
            payment context (external_ref, payment link, lifecycle
            history, cancel action). */}
        {canOpenDrawer && (
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="shrink-0 rounded p-1 text-muted transition hover:bg-surface-muted hover:text-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            aria-label={t("sale.timelineOpenDetails")}
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
      {drawerOpen && orderId && (
        <TimelinePaymentDrawer
          payment={p}
          currency={currency}
          locationId={locationId}
          orderId={orderId}
          customerPhone={customerPhone}
          onClose={() => setDrawerOpen(false)}
          onCancelPayment={onCancelPayment}
          onPrintReceipt={onPrintReceipt}
        />
      )}
    </div>
  );
}

// PaymentHistory + kindLabel moved to TimelinePaymentDrawer.tsx - the
// lifecycle audit list now renders inside the drawer where it has
// the room to breathe.
